import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { listPerks, handOverPerk, getLevelRewards, setLevelRewards } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Check, Save } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");

export default function MemberRewards() {
  const { activeFacility, user } = useAuth();
  const isMaster = atLeast(user?.role, "master_admin");
  const [ready, setReady] = useState(null);
  const [done, setDone] = useState([]);
  const [codes, setCodes] = useState({});
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([listPerks("ready"), listPerks("claimed")]);
      setReady(r.perks);
      setDone(c.perks.slice(0, 30));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load rewards");
      setReady([]);
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);
  useEffect(() => { getLevelRewards().then(setCfg).catch(() => setCfg(null)); }, []);

  const handOver = async (p) => {
    const typed = (codes[p.id] || "").trim().toUpperCase();
    if (typed && typed !== p.code) { toast.error("That code doesn't match the member's app"); return; }
    try {
      await handOverPerk(p.id, typed ? null : "Handed over without checking the code");
      toast.success(`${p.title} marked as handed over`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        levels: Object.fromEntries(cfg.levels.map((l) => [String(l.n), { points: Number(l.points) || 0, perk: (l.perk || "").trim() || null }])),
        monthly: cfg.monthly.map((m) => ({ points: Number(m.points) || 0, perk: (m.perk || "").trim() || null })),
        season: Object.fromEntries(Object.entries(cfg.season || {}).map(([k, v]) => [k, Number(v) || 0])),
      };
      setCfg(await setLevelRewards(body));
      toast.success("Rewards saved. New level-ups use these amounts.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const setLevel = (i, patch) => setCfg({ ...cfg, levels: cfg.levels.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const setMonthly = (i, patch) => setCfg({ ...cfg, monthly: cfg.monthly.map((m, j) => (j === i ? { ...m, ...patch } : m)) });

  return (
    <div className="space-y-6" data-testid="member-rewards">
      <div>
        <div className="eyebrow">Member App · Rewards</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Member Rewards</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Members earn XP in the app. Each level-up pays reward points into their wallet, and some levels unlock a perk they collect here.
          The top three by XP each month at {activeFacility?.name || "this gym"} win too. Check the code on their screen, hand it over, mark it.
        </p>
      </div>

      <div className="card-surface p-5" data-testid="perks-ready">
        <div className="eyebrow mb-3">Waiting at the front desk</div>
        {ready === null ? <div className="text-sm text-zinc-500">Loading…</div> : ready.length === 0 ? (
          <div className="text-sm text-zinc-500">Nothing waiting. Perks appear here when members level up.</div>
        ) : (
          <div className="space-y-2">
            {ready.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/25 bg-amber-500/5 px-4 py-3">
                <Gift className="h-5 w-5 text-amber-300" />
                <div className="min-w-[200px] flex-1">
                  <div className="font-semibold text-zinc-100">{p.client_name || "Member"}: {p.title}</div>
                  <div className="text-xs text-zinc-500">{p.label} · since {fmt(p.created_at)}</div>
                </div>
                <Input value={codes[p.id] || ""} onChange={(e) => setCodes({ ...codes, [p.id]: e.target.value })}
                  placeholder="Code on their app" className="h-8 w-40 font-mono uppercase" />
                <Button size="sm" onClick={() => handOver(p)} data-testid={`perk-handover-${p.id}`}><Check className="mr-1 h-4 w-4" /> Handed over</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {done.length > 0 && (
        <div className="card-surface p-5">
          <div className="eyebrow mb-3">Recently handed over</div>
          <table className="w-full text-sm">
            <tbody>
              {done.map((p) => (
                <tr key={p.id} className="border-b border-[#161616]">
                  <td className="py-2 pr-3 text-zinc-200">{p.client_name}</td>
                  <td className="py-2 pr-3 text-zinc-300">{p.title}</td>
                  <td className="py-2 pr-3 text-zinc-500">{p.label}</td>
                  <td className="py-2 text-right text-zinc-500">{fmt(p.claimed_at)} · {p.claimed_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cfg && (
        <div className="card-surface p-5" data-testid="level-rewards">
          <div className="flex items-center justify-between">
            <div className="eyebrow">What each level pays (all gyms)</div>
            {isMaster && <Button size="sm" onClick={save} disabled={saving}><Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save"}</Button>}
          </div>
          {!isMaster && <p className="mt-1 text-xs text-zinc-500">Only a master admin can change these.</p>}
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr><th className="py-2">Level</th><th className="py-2">XP</th><th className="py-2">Points</th><th className="py-2">Perk (optional)</th></tr>
            </thead>
            <tbody>
              {cfg.levels.map((l, i) => (
                <tr key={l.n} className="border-t border-[#161616]">
                  <td className="py-2 pr-3 font-semibold text-zinc-100">{l.n}. {l.name}</td>
                  <td className="py-2 pr-3 text-zinc-500 tabular-nums">{l.xp.toLocaleString()}</td>
                  <td className="py-2 pr-3"><Input type="number" min={0} value={l.points ?? 0} disabled={!isMaster} onChange={(e) => setLevel(i, { points: e.target.value })} className="h-8 w-24" /></td>
                  <td className="py-2"><Input value={l.perk || ""} disabled={!isMaster} onChange={(e) => setLevel(i, { perk: e.target.value })} placeholder="None" className="h-8" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="eyebrow mt-6 mb-2">Monthly leaderboard prizes (each gym)</div>
          <table className="w-full text-sm">
            <tbody>
              {cfg.monthly.map((m, i) => (
                <tr key={i} className="border-t border-[#161616]">
                  <td className="py-2 pr-3 font-semibold text-zinc-100">#{i + 1}</td>
                  <td className="py-2 pr-3"><Input type="number" min={0} value={m.points ?? 0} disabled={!isMaster} onChange={(e) => setMonthly(i, { points: e.target.value })} className="h-8 w-24" /></td>
                  <td className="py-2"><Input value={m.perk || ""} disabled={!isMaster} onChange={(e) => setMonthly(i, { perk: e.target.value })} placeholder="None" className="h-8" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {cfg.season && (
            <>
              <div className="eyebrow mt-6 mb-2">Season payouts (each quarter, by the tier members finish on)</div>
              <div className="flex flex-wrap gap-4">
                {Object.entries(cfg.season).map(([tier, pts]) => (
                  <label key={tier} className="flex items-center gap-2 text-sm text-zinc-300">
                    {tier}
                    <Input type="number" min={0} value={pts} disabled={!isMaster} className="h-8 w-24"
                      onChange={(e) => setCfg({ ...cfg, season: { ...cfg.season, [tier]: e.target.value } })} />
                  </label>
                ))}
              </div>
            </>
          )}
          <p className="mt-3 text-xs text-zinc-500">After the top level, every 5,000 XP earns a prestige star and 500 points. Points are the same reward points members use on renewals.</p>
        </div>
      )}
    </div>
  );
}
